import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Navigation from '../../components/Navigation/Navigation'
import Sidebar from '../../components/Sidebar/Sidebar'
import AddItemMenu from '../../components/AddItemMenu/AddItemMenu'
import ItemModal from '../../components/ItemModal/ItemModal'
import { useCollection, useCollectionItems } from '../../hooks/useCollectionItems'
import { useCollections } from '../../hooks/useCollections'
import { useBreakpoints } from '../../hooks/useBreakpoints'
import { CollectionItemDetail } from '../../data/mockData'
import styles from './CollectionPage.module.css'
import layoutStyles from '../../styles/layout.module.css'
import buttonStyles from '../../styles/buttons.module.css'

export default function CollectionPage() {
  const { collectionId } = useParams()
  const navigate = useNavigate()
  const searchInputRef = useRef<HTMLInputElement>(null)
  const addItemButtonRef = useRef<HTMLButtonElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState<CollectionItemDetail | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  
  const { data: collectionsData } = useCollections()
  const { isPhone, isTablet } = useBreakpoints()
  const isMobile = isPhone || isTablet
  
  const {
    data: collection,
    isLoading: collectionLoading,
    error: collectionError
  } = useCollection(collectionId!)
  
  const {
    data: itemsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: itemsLoading,
    error: itemsError
  } = useCollectionItems(collectionId!)
  
  // Flatten all pages of items
  const allItems = itemsData?.pages.flatMap(page => page.items) ?? []

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop
        >= document.documentElement.offsetHeight - 1000 &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage()
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Command+K (Mac) or Ctrl+K (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault()
        searchInputRef.current?.focus()
        return
      }

      // Check if up arrow key is pressed
      if (event.key === 'ArrowUp') {
        const activeElement = document.activeElement
        
        // Check if user is focused on any interactive element
        const isFocusedOnInteractive = activeElement && (
          activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'BUTTON' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.tagName === 'SELECT' ||
          activeElement.tagName === 'A' ||
          activeElement.getAttribute('contenteditable') === 'true' ||
          activeElement.getAttribute('tabindex') !== null
        )
        
        // Check if user is at the top of the page (not scrolled down)
        const isAtTop = window.scrollY <= 10 // Allow small tolerance for scroll position
        
        if (!isFocusedOnInteractive && isAtTop && collectionsData) {
          event.preventDefault()
          
          const currentIndex = collectionsData.collections.findIndex(c => c.id === collectionId)
          
          if (currentIndex > 0) {
            // Navigate to previous collection
            const previousCollection = collectionsData.collections[currentIndex - 1]
            navigate(`/collection/${previousCollection.id}`)
          } else if (currentIndex === 0) {
            // If we're on the first collection, navigate to recently added page
            navigate('/recently-added')
          }
        }
      }

      // Check if down arrow key is pressed
      if (event.key === 'ArrowDown') {
        const activeElement = document.activeElement
        
        // Check if user is focused on any interactive element
        const isFocusedOnInteractive = activeElement && (
          activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'BUTTON' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.tagName === 'SELECT' ||
          activeElement.tagName === 'A' ||
          activeElement.getAttribute('contenteditable') === 'true' ||
          activeElement.getAttribute('tabindex') !== null
        )
        
        // Check if user is at the top of the page (not scrolled down)
        const isAtTop = window.scrollY <= 10 // Allow small tolerance for scroll position
        
        if (!isFocusedOnInteractive && isAtTop && collectionsData) {
          event.preventDefault()
          
          const currentIndex = collectionsData.collections.findIndex(c => c.id === collectionId)
          
          if (currentIndex >= 0 && currentIndex < collectionsData.collections.length - 1) {
            // Navigate to next collection
            const nextCollection = collectionsData.collections[currentIndex + 1]
            navigate(`/collection/${nextCollection.id}`)
          }
          // If we're on the last collection, don't navigate anywhere (stay on current page)
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [collectionId, navigate, collectionsData])

  const handleAddLink = (url: string) => {
    console.log('Adding link to collection:', collectionId, url)
  }

  const handleUploadFile = () => {
    console.log('Uploading file to collection:', collectionId)
  }

  const handleItemClick = (item: CollectionItemDetail) => {
    setSelectedItem(item)
    setIsModalOpen(true)
  }

  const handleItemSelect = (item: CollectionItemDetail) => {
    setSelectedItem(item)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setSelectedItem(null)
  }

  if (collectionLoading) {
    return (
      <div className={layoutStyles.mainLayout}>
        <Sidebar />
        <div className={layoutStyles.mainContentWrapper}>
          <Navigation />
          <div className={styles.loadingSpinner}>
            <div className={styles.spinner}></div>
            <p>Loading collection...</p>
          </div>
        </div>
      </div>
    )
  }
  
  if (collectionError || !collection) {
    return (
      <div className={layoutStyles.mainLayout}>
        <Sidebar />
        <div className={layoutStyles.mainContentWrapper}>
          <Navigation />
          <div className={styles.collectionNotFound}>
            <h1>Collection not found</h1>
            <button onClick={() => navigate('/')}>Go back home</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={layoutStyles.mainLayout}>
      <Sidebar activeItem={collectionId} />
      
      <div className={layoutStyles.mainContentWrapper}>
        {/* Fixed Settings Button - Hidden on mobile and tablet */}
        {!isMobile && (
          <button className={buttonStyles.settingsButton}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_240_12481)">
                <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="#F3F3F3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke="#F3F3F3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </g>
              <defs>
                <clipPath id="clip0_240_12481">
                  <rect width="24" height="24" fill="white"/>
                </clipPath>
              </defs>
            </svg>
          </button>
        )}

        <header className={styles.collectionHeaderNav}>
          <div className={isPhone || isTablet ? styles.collectionNavContentMobile : styles.collectionNavContent}>
            <div className={isPhone ? styles.collectionLogoMobile : styles.collectionLogo}>
              HeyGather
            </div>
            
            <div className={isPhone ? styles.collectionSearchContainerMobile : styles.collectionSearchContainer}>
              <div className={styles.collectionSearchInputWrapper}>
                <svg className={styles.searchIcon} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.5 17.5L12.5 12.5M14.1667 8.33333C14.1667 11.555 11.555 14.1667 8.33333 14.1667C5.11167 14.1667 2.5 11.555 2.5 8.33333C2.5 5.11167 5.11167 2.5 8.33333 2.5C11.555 2.5 14.1667 5.11167 14.1667 8.33333Z" stroke="#303030" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {!isPhone && !isTablet && <span className={styles.collectionNameLabel}>{collection.title}</span>}
                {!isPhone && !isTablet && <div className={styles.searchSeparator}>|</div>}
                <input 
                  ref={searchInputRef}
                  type="text" 
                  placeholder={isPhone || isTablet ? 'Search or ask anything' : 'Search or ask anything (⌘ + K)'}
                  className={isPhone ? styles.collectionSearchInputMobile : styles.collectionSearchInput}
                />
              </div>
              <div style={{ position: 'relative' }}>
                <button 
                  ref={addItemButtonRef}
                  className={isPhone ? styles.collectionAddItemButtonMobile : styles.collectionAddItemButton}
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  aria-expanded={isMenuOpen}
                  aria-haspopup="dialog"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 3.33334V12.6667M3.33334 8H12.6667" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {!isPhone && "Add Item"}
                </button>
                <AddItemMenu
                  isOpen={isMenuOpen}
                  onClose={() => setIsMenuOpen(false)}
                  anchorElement={addItemButtonRef.current}
                  onAddLink={handleAddLink}
                  onUploadFile={handleUploadFile}
                />
              </div>
            </div>
          </div>
        </header>

        <main className={styles.collectionContent}>
          <div className={layoutStyles.container}>
            {itemsLoading && allItems.length === 0 && (
              <div className={styles.loadingSpinner}>
                <div className={styles.spinner}></div>
                <p>Loading items...</p>
              </div>
            )}
            
            {itemsError && (
              <div className={styles.errorMessage}>
                <p>Failed to load items. Please try again.</p>
              </div>
            )}
            
            {allItems.length > 0 && (
              <div className={styles.collectionItemsGrid}>
                {allItems.map(item => (
                  <div 
                    key={item.id} 
                    className={styles.collectionItemCard}
                    onClick={() => handleItemClick(item)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className={styles.collectionItemCardContent}>
                      <div className={styles.collectionItemImage}>
                        <img src={item.imagePath} alt={item.name} />
                      </div>
                      <div className={styles.collectionItemInfo}>
                        <h3 className={styles.collectionItemTitle}>{item.name}</h3>
                        {item.url && (
                          <a 
                            href={item.url} 
                            className={styles.collectionItemUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {item.url}
                          </a>
                        )}
                        <p className={styles.collectionItemDescription}>{item.description}</p>
                        <p className={styles.collectionItemDate}>{item.dateCreated}</p>
                      </div>
                    </div>
                  </div>
                ))}
                
                {isFetchingNextPage && (
                  <div className={styles.loadingSpinner}>
                    <div className={styles.spinner}></div>
                    <p>Loading more items...</p>
                  </div>
                )}
                
                {!hasNextPage && allItems.length > 0 && (
                  <div className={styles.endOfItems}>
                    <p>You've seen all items in this collection</p>
                  </div>
                )}
              </div>
            )}
            
            {!itemsLoading && allItems.length === 0 && !itemsError && (
              <div className={styles.endOfItems}>
                <p>No items in this collection</p>
              </div>
            )}
          </div>
        </main>
      </div>

      <ItemModal
        item={selectedItem}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        allItems={allItems}
        onItemSelect={handleItemSelect}
      />
    </div>
  )
}